import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, fmtMoney, formatApiError, DOC_TYPE_META } from "@/lib/api";
import { PageHeader, Content, StatusBadge } from "@/components/PageChrome";
import {
  Plus, TrendingUp, FileText, Receipt, ClipboardList, Users, ArrowUpRight,
  AlertCircle, Building2,
} from "lucide-react";
import { useCompanies } from "@/context/CompanyContext";

const kpiTiles = [
  { key: "total_billed", label: "Total Billed", icon: TrendingUp, format: "money", accent: "text-[var(--fg)]" },
  { key: "total_paid", label: "Total Paid", icon: TrendingUp, format: "money", accent: "text-green-700" },
  { key: "total_unpaid", label: "Outstanding", icon: AlertCircle, format: "money", accent: "text-amber-700" },
  { key: "total_gst", label: "GST / Tax", icon: FileText, format: "money", accent: "text-blue-700" },
  { key: "total_receipts", label: "Receipts", icon: Receipt, format: "money", accent: "text-[var(--fg)]" },
];

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const { scopeParams, scopeLabel, scopeIsAll, scopeIds, scopeVersion, version, focusScopeCompany } =
    useCompanies();
  const nav = useNavigate();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // One request carries both views: the headline totals are scoped to the
        // selection, while by_company always covers the whole account.
        const res = await api.get("/dashboard/summary", { params: scopeParams });
        if (!cancelled) {
          setData(res.data);
          setError("");
        }
      } catch (e) {
        if (!cancelled) {
          setError(formatApiError(e));
          setData(null);
        }
      }
    })();
    return () => { cancelled = true; };
    // `version` changes when the active company is switched, `scopeVersion` when
    // the view scope changes.
  }, [version, scopeVersion]); // eslint-disable-line react-hooks/exhaustive-deps

  if (error && !data) {
    return (
      <>
        <PageHeader overline="Overview" title="Dashboard" testid="page-dashboard" />
        <Content><div className="text-sm text-red-600" data-testid="dashboard-error">{error}</div></Content>
      </>
    );
  }

  if (!data) {
    return (
      <>
        <PageHeader overline="Overview" title="Dashboard" testid="page-dashboard" />
        <Content><div className="text-sm text-gray-500">Loading…</div></Content>
      </>
    );
  }

  const { counts, totals, by_type, recent, by_company = [], companies = [] } = data;
  // Currency comes from the profiles when every company in view agrees on one.
  // Mixed currencies are still added together, and the banner above says so
  // rather than letting the number imply a single unit.
  const currencies = data.scoped_currencies?.length
    ? data.scoped_currencies
    : [...new Set(companies.map((c) => c.base_currency).filter(Boolean))];
  const currency = currencies.length === 1 ? currencies[0] : "INR";
  const mixedCurrency = currencies.length > 1;

  const invoiceTypes = ["proforma", "sales", "timesheet", "recurring", "progress", "commercial", "debit_note"];
  const receiptTypes = ["payment_receipt", "expense_receipt"];
  // The column is worth showing only when the current selection actually spans
  // more than one company. Account size alone is not enough: a single-company
  // selection inside a big account has nothing to disambiguate.
  const showCompanyColumn = scopeIsAll || scopeIds.length > 1;

  const companiesShown = by_company.filter((c) => c.counts.documents > 0);
  const companiesIdle = by_company.filter((c) => c.counts.documents === 0);

  return (
    <>
      <PageHeader
        overline="Overview"
        title="Dashboard"
        description={
          by_company.length === 0
            ? "Set up your company profile to get started."
            : `Totals for ${scopeLabel} · ${companies.length} compan${companies.length === 1 ? "y" : "ies"} on this account`
        }
        testid="page-dashboard"
        actions={
          <>
            <Link to="/app/companies" className="text-sm px-4 py-2 border border-[var(--border)] rounded-md hover:bg-white" data-testid="btn-company">
              Companies
            </Link>
            <Link to="/app/documents/new/sales" className="btn-primary text-sm inline-flex items-center gap-2" data-testid="btn-new-invoice">
              <Plus className="w-4 h-4" /> New Invoice
            </Link>
          </>
        }
      />
      <Content>
        {by_company.length === 0 && (
          <div className="card-flat p-5 mb-6 border-l-4 border-l-[var(--primary)]" data-testid="banner-setup">
            <div className="flex items-center justify-between gap-4">
              <div>
                <div className="font-medium">Complete your company profile</div>
                <div className="text-sm text-gray-600 mt-1">Add your business name, GSTIN, address and bank details so we can put them on every invoice.</div>
              </div>
              <Link to="/app/companies" className="btn-primary text-sm">Set up now</Link>
            </div>
          </div>
        )}

        {/*
          Mixed currencies cannot be summed meaningfully, so the tiles below are
          labelled rather than silently assuming one currency.
        */}
        <div className="mb-4 flex items-center gap-2 text-xs text-gray-500 flex-wrap" data-testid="dashboard-scope">
          <Building2 className="w-3.5 h-3.5 shrink-0" />
          <span>
            Totals cover <span className="font-medium text-gray-700">{scopeLabel}</span>
            {scopeIsAll ? " (every company)" : ""}
          </span>
          {mixedCurrency && (
            <span className="text-amber-700">
              · mixed currencies ({currencies.join(", ")}) are added together
            </span>
          )}
        </div>

        {/* KPI tiles */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          {kpiTiles.map((k) => {
            const Icon = k.icon;
            return (
              <div key={k.key} className="card-flat p-5 hover-lift" data-testid={`kpi-${k.key}`}>
                <div className="flex items-center justify-between mb-4">
                  <div className="overline">{k.label}</div>
                  <Icon className="w-4 h-4 text-gray-400" />
                </div>
                <div className={`text-3xl font-medium num ${k.accent}`}>{fmtMoney(totals[k.key], currency)}</div>
              </div>
            );
          })}
        </div>

        {/* Counts strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
          {[
            { label: "Invoices", value: counts.invoices, icon: FileText, to: "/app/invoices" },
            { label: "Receipts", value: counts.receipts, icon: Receipt, to: "/app/receipts" },
            { label: "Purchase Orders", value: counts.purchase_orders, icon: ClipboardList, to: "/app/purchase-orders" },
            // Account-wide, not per company: clients are shared across profiles.
            { label: "Clients (all companies)", value: counts.clients, icon: Users, to: "/app/clients" },
          ].map((c) => {
            const Icon = c.icon;
            return (
              <Link key={c.label} to={c.to} className="card-flat p-4 hover-lift flex items-center justify-between" data-testid={`count-${c.label.toLowerCase().replace(/\s/g, "-")}`}>
                <div>
                  <div className="overline">{c.label}</div>
                  <div className="text-2xl font-medium mt-2 num">{c.value}</div>
                </div>
                <Icon className="w-6 h-6 text-gray-300" />
              </Link>
            );
          })}
        </div>

        {/* Breakdown & Recent */}
        <div className="grid lg:grid-cols-3 gap-4 mt-8">
          <div className="lg:col-span-2 card-flat overflow-hidden">
            <div className="px-5 py-4 border-b border-[var(--border)] flex items-center justify-between">
              <div>
                <div className="overline">Recent Activity</div>
                <div className="text-lg font-medium mt-1">Latest documents</div>
              </div>
              <Link to="/app/invoices" className="text-xs text-gray-500 inline-flex items-center gap-1 hover:text-[var(--fg)]" data-testid="link-view-all">
                View all <ArrowUpRight className="w-3 h-3" />
              </Link>
            </div>
            {recent.length === 0 ? (
              <div className="p-8 text-center text-sm text-gray-500">No documents yet. Create your first one.</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="text-left text-[11px] text-gray-500 uppercase tracking-wider">
                  <tr className="border-b border-[var(--border)]">
                    <th className="px-5 py-3 font-medium">Number</th>
                    <th className="px-5 py-3 font-medium">Type</th>
                    {showCompanyColumn && (
                      <th className="px-5 py-3 font-medium">Company</th>
                    )}
                    <th className="px-5 py-3 font-medium">Client</th>
                    <th className="px-5 py-3 font-medium text-right">Amount</th>
                    <th className="px-5 py-3 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody data-testid="recent-table">
                  {recent.map((d) => (
                    <tr key={d.id} className="border-b border-[var(--border)] hover:bg-gray-50">
                      <td className="px-5 py-3 font-mono text-[13px]">
                        <Link to={`/app/documents/${d.id}`} className="hover:underline">{d.number}</Link>
                      </td>
                      <td className="px-5 py-3 text-gray-600">{DOC_TYPE_META[d.doc_type]?.short || d.doc_type}</td>
                      {showCompanyColumn && (
                        <td className="px-5 py-3 text-gray-600">{d.company_name || "—"}</td>
                      )}
                      <td className="px-5 py-3">{d.client_snapshot?.name || "-"}</td>
                      <td className="px-5 py-3 text-right num">{fmtMoney(d.total, d.currency)}</td>
                      <td className="px-5 py-3"><StatusBadge status={d.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="card-flat p-5">
            <div className="overline">Documents by type</div>
            <div className="mt-4 space-y-3">
              {[...invoiceTypes, ...receiptTypes, "purchase_order", "credit_note"].map((t) => (
                <div key={t} className="flex items-center justify-between text-sm">
                  <span className="text-gray-700">{DOC_TYPE_META[t]?.short || t}</span>
                  <span className="font-mono text-[13px]">
                    <span className="text-gray-400 mr-2">{by_type[t]?.count || 0}</span>
                    <span>{fmtMoney(by_type[t]?.total || 0, currency)}</span>
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/*
          Whole-account monitoring. This is deliberately NOT filtered by the
          scope selection: the tiles above answer "how is what I am looking at
          doing", while this answers "how is everything on this account doing".
          Each row links into that company's documents, so it is also the
          shortcut for switching between companies without touching the picker.
        */}
        {companies.length > 1 && (
          <div className="card-flat overflow-hidden mt-8" data-testid="by-company">
            <div className="px-5 py-4 border-b border-[var(--border)] flex items-center justify-between flex-wrap gap-2">
              <div>
                <div className="overline">All Companies</div>
                <div className="text-lg font-medium mt-1">Every company on this account</div>
              </div>
              <Link to="/app/companies" className="text-xs text-gray-500 inline-flex items-center gap-1 hover:text-[var(--fg)]">
                Manage companies <ArrowUpRight className="w-3 h-3" />
              </Link>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[820px]">
                <thead className="text-left text-[11px] text-gray-500 uppercase tracking-wider">
                  <tr className="border-b border-[var(--border)]">
                    <th className="px-5 py-3 font-medium">Company</th>
                    <th className="px-5 py-3 font-medium text-right">Docs</th>
                    <th className="px-5 py-3 font-medium text-right">Invoices</th>
                    <th className="px-5 py-3 font-medium text-right">Receipts</th>
                    <th className="px-5 py-3 font-medium text-right">Billed</th>
                    <th className="px-5 py-3 font-medium text-right">Paid</th>
                    <th className="px-5 py-3 font-medium text-right">Outstanding</th>
                    <th className="px-5 py-3"></th>
                  </tr>
                </thead>
                <tbody data-testid="by-company-table">
                  {companiesShown.map((c) => {
                    const cc = c.base_currency || "INR";
                    const inScope = scopeIsAll || scopeIds.includes(c.company_id);
                    return (
                      <tr
                        key={c.company_id || "none"}
                        className={`border-b border-[var(--border)] hover:bg-gray-50 ${inScope ? "" : "opacity-60"}`}
                        data-testid={`by-company-${c.company_id || "none"}`}
                      >
                        <td className="px-5 py-3">
                          <div className="font-medium truncate">{c.company_name}</div>
                          <div className="text-[11px] text-gray-500 truncate">
                            {c.company_label || cc}
                            {!inScope && " · outside current filter"}
                          </div>
                        </td>
                        <td className="px-5 py-3 text-right num">{c.counts.documents}</td>
                        <td className="px-5 py-3 text-right num">{c.counts.invoices}</td>
                        <td className="px-5 py-3 text-right num">{c.counts.receipts}</td>
                        <td className="px-5 py-3 text-right num">{fmtMoney(c.totals.total_billed, cc)}</td>
                        <td className="px-5 py-3 text-right num text-green-700">{fmtMoney(c.totals.total_paid, cc)}</td>
                        <td className="px-5 py-3 text-right num text-amber-700">
                          {fmtMoney(c.totals.total_unpaid, cc)}
                        </td>
                        <td className="px-5 py-3 text-right whitespace-nowrap">
                          {c.company_id ? (
                            <button
                              type="button"
                              onClick={() => {
                                focusScopeCompany(c.company_id);
                                nav("/app/invoices");
                              }}
                              className="text-xs text-gray-500 hover:text-[var(--fg)]"
                              data-testid={`by-company-open-${c.company_id}`}
                              title={`Open invoices for ${c.company_name}`}
                            >
                              View <ArrowUpRight className="w-3 h-3 inline" />
                            </button>
                          ) : (
                            <span className="text-xs text-gray-400" title="Legacy documents with no company">
                              —
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {companiesIdle.length > 0 && (
              <div className="px-5 py-3 border-t border-[var(--border)] bg-gray-50/60 text-xs text-gray-500" data-testid="by-company-idle">
                No documents yet:{" "}
                {companiesIdle.map((c) => c.company_name).filter(Boolean).join(", ")}
              </div>
            )}
          </div>
        )}
      </Content>
    </>
  );
}
